import * as AppPics from 'assets/images';
import { AppButton, AppTextField } from 'components';
import DOMPurify from 'dompurify';
import useComments from 'hooks/API/GET/useComments';
import useManageComments from 'hooks/API/useManageComments';
import useReviewComment from 'hooks/API/useReviewComment';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { useState, useEffect, createRef } from 'react';
import { contentErrors } from 'utilities/constants';
import { DropdownMenu, DropdownItem } from 'reactstrap';
import { AiFillEdit, AiOutlineDelete } from 'react-icons/ai';
import { TiTick, TiTimes } from 'react-icons/ti';
import VDots from 'components/DropDowns/VDots';
import Editor from 'components/CKEditor/Editor';
import CommentBoxParent from './CommentBoxParent';
import dayjs from 'dayjs';

function VideoCommentDropBox({ open, data = {}, close }) {
  const { apiBodyProps = {}, replyModal = {}, currentTimestamp } = data;
  const { apiHash } = replyModal;

  const ref = createRef();
  useOnClickOutside(ref, () => close());

  const [inMin, setInMin] = useState('');
  const [inSec, setInSec] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewDetails, setReviewDetails] = useState({});
  const [reply, setReply] = useState('');
  const [editReply, setEditReply] = useState({});

  const { isLoading, data: response = {} } = useComments({ enabled: !!apiHash, hashID: apiHash });

  const { isLoading: isAdding, mutate } = useReviewComment();

  const manageReply = useManageComments();

  useEffect(() => {
    // NOTE: Currently, "currentTimestamp" is in seconds
    if (currentTimestamp) {
      const minutes = Math.floor((currentTimestamp || 0) / 60);
      const seconds = currentTimestamp - minutes * 60;
      setInMin(`${minutes}`);
      setInSec(`${Math.floor(seconds)}`);
    }
  }, [currentTimestamp]);

  useEffect(() => {
    if (editReply?.replyID) {
      setReply(editReply.text);
    }
  }, [editReply]);

  useEffect(() => {
    if (response?.data?._id) {
      setReviewDetails(response.data);
      setReviewComment(response.data?.reviewComment || '');
      if (response.data?.timestamp?.inMin) setInMin(response.data?.timestamp?.inMin);
      if (response.data?.timestamp?.inSec) setInSec(response.data?.timestamp?.inSec);
    }
  }, [response?.data]);

  const Options = [];

  for (const [key, value] of Object.entries(contentErrors)) {
    Options.push(<option value={key}>{value}</option>);
  }

  function clear() {
    setReviewComment('');
    close();
  }

  function handleSave(args = {}) {
    if (manageReply.isLoading) return;
    if (!`${reviewComment}`.trim()) return;

    const apiData = {
      reviewComment: `${reviewComment}`.trim(),
      ...apiBodyProps,
      _id: reviewDetails?._id,
      type: reviewDetails?._id ? 'UPDATE' : 'CREATE',
      ...args,
    };

    manageReply.mutate(apiData, {
      onSuccess() {
        clear();
      },
    });
  }

  function handleReply(args = {}) {
    if (isAdding) return;
    if (!reviewDetails?._id) return;
    if (!`${reply}`.trim()) return;

    const apiData = {
      ...args,
      _id: reviewDetails._id,
      comment: `${reply}`.trim(),
    };

    mutate(apiData, {
      onSuccess() {
        setReply('');
      },
    });
  }

  function modifyReply(args = {}) {
    if (isAdding) return;

    const apiData = {
      ...args,
      _id: reviewDetails._id,
    };

    mutate(apiData, {
      onSuccess() {
        setEditReply({});
        setReply('');
      },
    });
  }

  return (
    <>
      <CommentBoxParent ref={ref} open={open}>
        <div className="modal-dialog modal-dialog-centered uploaddialog">
          <div className="modal-content p-3">
            <div className="modal-head">
              <h2 className="popuptitle">{isLoading && 'Loading...'}</h2>

              <AppButton variantClass="btn btn-primary cancelbtn commentCloseBtn" onClick={close}>
                <img src={AppPics.CloseSvg} alt="" />
              </AppButton>
            </div>
            <div className="modal-body p-0 __review__blocks_quotes">
              <div className="container p-0">
                {(editMode || !apiHash) && (
                  <div className="commentsection">
                    <div className="w-100 mt-3">
                      <div className="row">
                        <div className="col-6">
                          <AppTextField
                            labelProps={{ label: 'Min' }}
                            inputProps={{
                              placeholder: 'Minute',
                              value: inMin,
                              onChange: (event) => setInMin(event.target.value),
                            }}
                            // fieldErrors={errors['name']}
                          />
                        </div>
                        <div className="col-6">
                          <AppTextField
                            labelProps={{ label: 'Sec' }}
                            inputProps={{
                              placeholder: 'Seconds',
                              value: inSec,
                              onChange: (event) => setInSec(event.target.value),
                            }}
                            // fieldErrors={errors['name']}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="w-100 mt-3 ">
                      <Editor
                        data={{
                          value: reviewComment || '',
                          onChange: (editorData) => {
                            setReviewComment(editorData);
                          },
                        }}
                        config={{
                          placeholder: 'Please provide your comments here',
                          toolbar: ['bold', 'italic', 'uploadImage'],
                        }}
                      />
                    </div>
                    <AppButton
                      variantClass="btn btn-primary savebutton mt-3"
                      onClick={() => {
                        handleSave({ modalType: 'VIDEO', timestamp: { inMin, inSec } });
                      }}
                    >
                      Save
                    </AppButton>
                  </div>
                )}
                {!editMode && apiHash && (
                  <div className="jumbotron m-0 p-0 bg-transparent mt-3">
                    <div className="row m-0 p-0 ">
                      <div className="col-12 p-0 m-0 ">
                        <div className="card border-0 rounded">
                          <div className="card-header p-1 bg-light border border-top-0 border-left-0 border-right-0">
                            <h4 className="float-left">
                              {' '}
                              <span className="mx-2">Timestamp :</span>
                              <span>
                                {`0${reviewDetails?.timestamp?.inMin || 0}`.slice(-2)}:{' '}
                                {`0${reviewDetails?.timestamp?.inSec || 0}`.slice(-2)}
                              </span>
                            </h4>
                            <p className="float-left">
                              {' '}
                              <span className="mx-2">Comments :</span>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(reviewDetails?.reviewComment),
                                }}
                              ></span>
                            </p>

                            <AppButton onClick={() => handleSave({ resolved: true })}>
                              <img className="tickicon" src={AppPics.TickMarkSvg} alt="" />
                            </AppButton>

                            <div className="dropdown fade show">
                              <VDots>
                                <DropdownMenu>
                                  <DropdownItem
                                    onClick={() => {
                                      setEditMode(true);
                                    }}
                                  >
                                    <AiFillEdit />
                                    Edit
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => {
                                      const confirmed = window.confirm(
                                        'Please confirm your action'
                                      );

                                      if (confirmed) {
                                        handleSave({ type: 'DELETE' });
                                      }
                                    }}
                                  >
                                    <AiOutlineDelete />
                                    Delete
                                  </DropdownItem>
                                </DropdownMenu>
                              </VDots>
                            </div>
                          </div>
                          <div className="card bg-sohbet border-0 m-0 p-4 px-5">
                            <div
                              id="sohbet"
                              className="card border-0 m-0 p-0 position-relative bg-transparent"
                            >
                              {Array.isArray(reviewDetails?.replyComment) &&
                                reviewDetails?.replyComment?.map((item) => {
                                  return (
                                    <div
                                      className="balon2 p-2 m-0 position-relative"
                                      key={item._id}
                                    >
                                      {' '}
                                      <span className="float-left sohbet2">
                                        <span
                                          className="sohbet3"
                                          dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(item.text),
                                          }}
                                        ></span>
                                        {!item.freeze && (
                                          <VDots>
                                            <DropdownMenu>
                                              <DropdownItem
                                                onClick={() => {
                                                  setEditReply({
                                                    replyID: item._id,
                                                    text: item.text,
                                                  });
                                                }}
                                              >
                                                <AiFillEdit />
                                                Edit
                                              </DropdownItem>
                                              <DropdownItem
                                                onClick={() => {
                                                  const confirmed = window.confirm(
                                                    'Please confirm your action'
                                                  );

                                                  if (confirmed) {
                                                    modifyReply({
                                                      type: 'DELETE',
                                                      configs: {
                                                        params: { replyID: item._id },
                                                      },
                                                    });
                                                  }
                                                }}
                                              >
                                                <AiOutlineDelete />
                                                Delete
                                              </DropdownItem>
                                            </DropdownMenu>
                                          </VDots>
                                        )}
                                      </span>
                                      {item?.createdBy?.name && (
                                        <div className="text-right">
                                          <small>
                                            <em>{item?.createdBy?.name}</em>{' '}
                                            {item?.createdOn && (
                                              <>
                                                {dayjs(item.createdOn).format(
                                                  'DD MMM YYYY, h:mm a'
                                                )}
                                              </>
                                            )}
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                          <div className="w-100 card-footer p-0 bg-light border border-bottom-0 border-left-0 border-right-0">
                            <form className="m-0 p-0" autoComplete="off">
                              <div className="row m-0 p-0">
                                <div className="col-9 m-0 p-1">
                                  <Editor
                                    data={{
                                      value: reply || '',
                                      onChange: (editorData) => {
                                        setReply(editorData);
                                      },
                                    }}
                                    config={{
                                      placeholder: 'Type a message...',
                                      toolbar: ['bold', 'italic', 'uploadImage'],
                                    }}
                                  />
                                </div>
                                <div className="col-3 m-0 p-1">
                                  {!editReply?.replyID && (
                                    <AppButton
                                      variantClass="btn btn-outline-secondary rounded border w-100  replybtn"
                                      title="Click to reply"
                                      onClick={() => handleReply()}
                                    >
                                      Reply
                                    </AppButton>
                                  )}
                                  {editReply?.replyID && (
                                    <>
                                      <AppButton
                                        variantClass="btn btn-success mx-2"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => {
                                          modifyReply({
                                            type: 'UPDATE',
                                            replyID: editReply.replyID,
                                            reply,
                                          });
                                        }}
                                      >
                                        <TiTick />
                                      </AppButton>
                                      <AppButton
                                        variantClass="btn btn-danger mx-2"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => {
                                          setEditReply({});
                                          setReply('');
                                        }}
                                      >
                                        <TiTimes />
                                      </AppButton>
                                    </>
                                  )}
                                </div>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CommentBoxParent>
    </>
  );
}

export default VideoCommentDropBox;
